<template>
    <div 
        class="w-full md:w-auto flex justify-start pb-6 pt-4"
        :class="{'hidden': previousPage < 1}"
    >
        <ProductListingInfiniteScroll
            :page="previousPage"
            :limit="listing?.limit"
            :sorting="listing?.currentSorting"
            :total="listing?.total"
            :current-filters="listing?.currentFilters"
            :direction="'prev'"
            @update:listing="updateListing"
        />
    </div>

    <template 
        v-for="pageInfo in currentListings" 
        :key="pageInfo"
    >
        <ProductListingPage
            class="listing-page"
            :products="pageInfo?.products"
            :page-number="pageInfo?.page"
            :grid-classes="props?.gridClasses"
            @update:url="updateOnlyUrl"
        />
    </template>

    <div 
        class="w-full md:w-auto flex justify-start mb-2"
        :class="{'hidden': nextPage > lastPage}"
    >
        <ProductListingInfiniteScroll
            class="h-1"
            :page="nextPage"
            :limit="listing?.limit"
            :sorting="listing?.currentSorting"
            :total="listing?.total"
            :current-filters="listing?.currentFilters"
            :direction="'next'"
            @update:listing="updateListing"
        />
    </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useNuxtApp, usePage } from '#imports'
import type { HblProductListing } from '@/utils/types'
import { useWindowScroll } from '@vueuse/core'

interface ProductListingProps {
    data: HblProductListing,
    total?: boolean,
    sorting?: boolean,
    gridClasses?: string
}

const props = withDefaults(defineProps<ProductListingProps>(), {
    total: true,
    sorting: true,
    gridClasses: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-4 gap-4 pb-4'
})

const listing = ref(props.data)
const currentListings = ref(
    [
        {
            'page': listing?.value?.page,
            'products': listing?.value?.products
        }
    ]
)

const { $hblBus } = useNuxtApp()
if (listing?.value?.products?.length > 0) {
    $hblBus.$emit('viewProductListing', { id: 'category', name: 'Category', products: listing.value.products })
}

function updateListing (newListing) {
    listing.value = newListing

    if(newListing?.page < activePage?.value) {
        currentListings.value = [
            {
                'page': newListing?.page,
                'products': newListing?.products
            }
        ].concat(currentListings.value)
        keepScrollPosition()
    }

    if(newListing.page > activePage.value) {
        currentListings.value = currentListings.value.concat([
            {
                'page': newListing?.page,
                'products': newListing?.products
            }
        ])
    }

    activePage.value = newListing.page
}

const { y } = useWindowScroll({ behavior: 'instant' })
function keepScrollPosition () {
    const listingPageHeight = document.querySelectorAll(".listing-page")[0]?.offsetHeight
    y.value = listingPageHeight + y.value
}

const { updateUri, mapParams } = usePage()
function updateOnlyUrl (newPageNumber: number) {
    try {
        const params =  mapParams(listing?.value?.currentFilters, listing?.value?.limit, listing?.value?.currentSorting, newPageNumber)
        updateUri(params)
        
    } catch (e) {
        showError(e)
    }
}

const previousPage = computed(() => {
    return currentListings?.value[0]?.page - 1
})

const nextPage = computed(() => {
    return currentListings?.value[currentListings?.value?.length-1]?.page + 1
})

const lastPage = Math.ceil(listing?.value?.total / listing?.value?.limit)

const activePage = ref(listing?.value?.page)
</script>
